/**
 * Écran d'ajout au journal — feuille modale (specs §6.3).
 *
 * Les **quatre voies** du §6.3 partagent un même écran, et toutes sont
 * fonctionnelles depuis l'étape 6 :
 *   a) Accès rapide — aliments récents et favoris, recettes favorites et récentes
 *   b) Recherche — recettes, base personnelle, puis Open Food Facts
 *   c) Scan de code-barres — caméra + zxing-wasm
 *   d) Saisie libre — quatre macros, sans fiche en base
 *
 * ⚠️ **L'onglet ouvert par défaut est « Accès rapide »**, comme le §6.3-a le
 * prescrit. C'était « Saisie libre » à l'étape 5, faute d'alternative : ouvrir
 * sur un écran vide aurait coûté un tap à chaque saisie. La bascule était actée
 * en clôture d'étape 5 et elle est faite ici.
 *
 * ⚠️ **Plusieurs étapes, une seule feuille** (specs §6.3 : « sans quitter
 * l'écran »). Choisir un aliment ne referme rien : le panneau de quantité
 * remplace la liste dans la même feuille, avec un retour explicite. Une recette
 * en ajoute un second — l'ajustement des ingrédients (§6.5-2) — au même endroit.
 * Empiler une seconde feuille modale serait une impasse gestuelle sur iPhone —
 * deux poignées de glissement superposées, deux voiles.
 *
 * ⚠️ Aucun total n'est envoyé au serveur (§5.1). Ce que l'utilisateur voit avant
 * validation est une prévisualisation (`lib/foodPreview.ts`), et le serveur
 * recalcule tout — pour une saisie libre comme pour un aliment.
 *
 * ⚠️ Le corps vit dans un composant **enfant de `Sheet`**, qui ne rend rien
 * quand la feuille est fermée. Fermer démonte donc l'état — y compris le
 * scanner, donc la caméra —, rouvrir le reconstruit à neuf.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type {
  AdjustEntryIngredientInput,
  LogEntryDTO,
  LogEntryInput,
  RecipeDTO,
} from '../../../../shared/types';
import { createFood, fetchFood, QUICK_ACCESS_KEY } from '../../api/foods';
import { Button } from '../../components/nourri';
import { ErrorNote, Hint, Sheet } from '../../components/ui/primitives';
import type { EntryPreview } from '../../hooks/useDay';
import { fieldsOf } from '../../lib/formFields';
import { FoodForm } from '../library/FoodEditor';
import { emptyFoodDraft, inputOfDraft, type FoodDraft } from '../library/foodDraft';
import type { AddCategory, AddPick, FoodPick, FoodTab, RecipeTab } from './addTypes';
import { FreeEntryForm } from './FreeEntryForm';
import { MealPanel } from './MealPanel';
import { QuantityStep } from './QuantityStep';
import { QuickAccessPanel } from './QuickAccessPanel';
import { RecipeBlockForm, RecipeEntryStep } from './RecipeEntryStep';
import { ScanPanel } from './ScanPanel';
import { SearchPanel } from './SearchPanel';

const CATEGORIES: { key: AddCategory; label: string }[] = [
  { key: 'food', label: 'Aliment' },
  { key: 'meal', label: 'Repas' },
  { key: 'recipe', label: 'Recette' },
];

/** Accès disponibles sous chaque catégorie. Le premier est celui qui s'ouvre. */
const FOOD_TABS: { key: FoodTab; label: string }[] = [
  { key: 'quick', label: 'Favoris & récents' },
  { key: 'search', label: 'Recherche' },
  { key: 'scan', label: 'Scanner' },
  { key: 'free', label: 'Saisie libre' },
];

const RECIPE_TABS: { key: RecipeTab; label: string }[] = [
  { key: 'quick', label: 'Favoris & récentes' },
  { key: 'search', label: 'Recherche' },
];

/**
 * Où en est l'ajout : parcourir, choisir une quantité, ajouter une recette, ou
 * créer une fiche.
 *
 * ⚠️ `recipe` est un état distinct de `quantity` parce que la séquence du §6.5
 * compte **deux** temps (quantité puis ajustement) là où un aliment n'en a
 * qu'un. `RecipeEntryStep` porte lui-même ce second temps.
 */
type Stage =
  | { kind: 'browse' }
  | { kind: 'quantity'; pick: FoodPick }
  | { kind: 'recipe'; recipe: RecipeDTO }
  | { kind: 'create'; draft: FoodDraft };

/**
 * Rangée de catégories — **trois parts égales**, jamais de retour à la ligne.
 *
 * C'est le premier choix de l'écran, donc celui qui doit se lire d'un coup :
 * trois boutons de largeur identique se pointent sans lire. Le retrait à la
 * ligne est exclu ici (contrairement aux accès en dessous) — une catégorie
 * seule sur sa ligne cesserait de se lire comme un choix entre trois.
 */
function CategoryStrip({
  category,
  onChange,
}: {
  category: AddCategory;
  onChange: (next: AddCategory) => void;
}) {
  return (
    /*
      Prototype : le contrôle segmenté de la feuille d'ajout — trois boutons de
      largeur égale, celui qui est choisi en pastille pleine.
    */
    <div role="tablist" aria-label="Catégorie d'ajout" className="n-segmented">
      {CATEGORIES.map((item) => {
        const active = category === item.key;
        return (
          <Button
            key={item.key}
            role="tab"
            aria-selected={active}
            variant={active ? 'selected' : 'secondary'}
            shape="rounded"
            onClick={() => {
              onChange(item.key);
            }}
          >
            {item.label}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * Rangée d'accès, sous la catégorie.
 *
 * ⚠️ Retour à la ligne autorisé, et nécessaire : « Favoris & récents /
 * Recherche / Scanner / Saisie libre » ne tient pas sur 393 px. Deux lignes de
 * pastilles restent lisibles là où des libellés tronqués ne le seraient pas.
 *
 * ⚠️ **La hiérarchie tient à la FORME, plus à la teinte.** Les catégories sont
 * des segments de largeur égale, les accès des pastilles qui se suivent — c'est
 * la distinction du prototype, et c'est celle qui survit au fait que le système
 * n'a **qu'une** façon de dire « celui-ci » (`variant="selected"`). Deux
 * manières de marquer un choix sur un même écran obligeraient à apprendre
 * laquelle veut dire quoi.
 */
function TabStrip<T extends string>({
  tabs,
  tab,
  onChange,
}: {
  tabs: readonly { key: T; label: string }[];
  tab: T;
  onChange: (tab: T) => void;
}) {
  if (tabs.length < 2) return null;
  return (
    <div role="tablist" aria-label="Mode d'ajout" className="n-chip-row">
      {tabs.map((item) => (
        <Button
          key={item.key}
          role="tab"
          aria-selected={tab === item.key}
          variant={tab === item.key ? 'selected' : 'secondary'}
          onClick={() => {
            onChange(item.key);
          }}
        >
          {item.label}
        </Button>
      ))}
    </div>
  );
}

/** Création d'une fiche depuis l'ajout : code-barres inconnu, ou rien trouvé. */
function CreateFoodStage({
  draft,
  onDraftChange,
  onCreated,
  onBack,
}: {
  draft: FoodDraft;
  onDraftChange: (next: FoodDraft) => void;
  onCreated: (pick: FoodPick) => void;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setSaving(true);
    setError(null);
    createFood(inputOfDraft(draft))
      .then((food) => {
        void queryClient.invalidateQueries({ queryKey: QUICK_ACCESS_KEY });
        void queryClient.invalidateQueries({ queryKey: ['foods'] });
        void queryClient.invalidateQueries({ queryKey: ['food-search'] });
        // Enchaînement direct sur la quantité : on vient de créer la fiche pour
        // l'ajouter, pas pour l'admirer.
        onCreated({ kind: 'food', food });
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <Button
        variant="secondary"
        shape="rounded"
        onClick={onBack}
        style={{ alignSelf: 'flex-start' }}
      >
        ‹ Retour
      </Button>
      {draft.barcode !== '' && <Hint>Code-barres {draft.barcode} — pré-rempli par le scan.</Hint>}
      {error !== null && <ErrorNote>{error}</ErrorNote>}
      <FoodForm
        draft={draft}
        onDraftChange={onDraftChange}
        onSubmit={save}
        saving={saving}
        submitLabel="Créer et choisir la quantité"
        footnote="La fiche rejoint votre base personnelle : elle sera trouvée en premier lors des prochaines recherches et des prochains scans."
      />
    </div>
  );
}

/**
 * Correction d'une entrée déjà enregistrée.
 *
 * ⚠️ Ce n'est **pas** une entorse à l'immuabilité (§4.1). La règle interdit
 * qu'un changement *ailleurs* — une fiche corrigée, un aliment supprimé —
 * modifie une entrée existante. Corriger explicitement l'entrée elle-même est
 * prévu : les macros sont re-produites par le serveur, puis figées à nouveau.
 *
 * ⚠️ Une entrée d'aliment dont la fiche a été **supprimée** depuis ne peut plus
 * être recalculée : elle se corrige alors en saisie libre, valeurs figées
 * pré-remplies. C'est dit à l'écran plutôt que subi.
 */
function CorrectionBody({
  entry,
  mealName,
  onSubmit,
  onAdjust,
}: {
  entry: LogEntryDTO;
  mealName: string;
  onSubmit: (input: LogEntryInput, preview: EntryPreview) => void;
  onAdjust: (lines: AdjustEntryIngredientInput[]) => void;
}) {
  const food = useQuery({
    queryKey: ['food', entry.foodId],
    queryFn: () => fetchFood(entry.foodId ?? 0),
    // Jamais pour une entrée de recette : son bloc se suffit à lui-même.
    enabled: entry.type === 'food' && entry.foodId !== null,
    retry: false,
  });

  /*
   * ⚠️ Une entrée de recette se corrige par son **bloc**, pas en repartant de la
   * recette (specs §6.5-d). La recette a pu changer, ou disparaître, depuis
   * l'ajout : la reprendre ferait entrer dans une entrée passée des valeurs qui
   * n'y étaient pas. Les lignes du bloc sont autonomes, et c'est tout ce qu'on
   * corrige ici. Le `useQuery` ci-dessus est désactivé pour ce type, donc il ne
   * part aucune requête.
   */
  if (entry.type === 'recipe' && entry.ingredients.length > 0) {
    return <RecipeBlockForm entry={entry} onSubmit={onAdjust} />;
  }

  if (entry.type === 'food' && entry.foodId !== null) {
    if (food.isPending) return <Hint>Chargement de la fiche…</Hint>;
    if (food.data) {
      return (
        <QuantityStep
          pick={{ kind: 'food', food: food.data }}
          mealName={mealName}
          initialQuantity={entry.quantity}
          initialPortionLabel={entry.portionLabel}
          onValidate={onSubmit}
        />
      );
    }
    // Fiche introuvable : on retombe sur la saisie libre, ci-dessous.
  }

  return (
    <>
      {entry.type === 'food' && (
        <Hint>
          La fiche d’origine n’existe plus. Les valeurs enregistrées sont conservées telles
          quelles ; les corriger transformera cette ligne en saisie libre.
        </Hint>
      )}
      {entry.type === 'recipe' && (
        <Hint>
          Cette entrée de recette ne porte plus aucun ingrédient. Les valeurs enregistrées sont
          conservées telles quelles ; les corriger transformera cette ligne en saisie libre.
        </Hint>
      )}
      <FreeEntryForm
        initialLabel={entry.label}
        initialMacros={fieldsOf(entry.macros)}
        submitLabel="Enregistrer la correction"
        onSubmit={onSubmit}
      />
    </>
  );
}

/** Corps de la feuille — monté à l'ouverture, démonté à la fermeture. */
function EntryBody({
  entry,
  mealId,
  mealName,
  onSubmit,
  onAdjust,
  onCopyMeal,
  onClose,
}: {
  entry: LogEntryDTO | undefined;
  /** Repas visé — sert à s'exclure soi-même des repas proposés à la copie. */
  mealId: number | undefined;
  mealName: string;
  onSubmit: (input: LogEntryInput, preview: EntryPreview) => void;
  onAdjust: (lines: AdjustEntryIngredientInput[]) => void;
  onCopyMeal: (sourceMealId: number) => void;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<AddCategory>('food');
  const [foodTab, setFoodTab] = useState<FoodTab>('quick');
  const [recipeTab, setRecipeTab] = useState<RecipeTab>('quick');
  const [stage, setStage] = useState<Stage>({ kind: 'browse' });

  const validate = (input: LogEntryInput, preview: EntryPreview) => {
    onSubmit(input, preview);
    // Fermeture immédiate, sans attendre le serveur : la mise à jour optimiste
    // du bandeau prend le relais (§6.2). C'est ce qui tient les 15 secondes.
    onClose();
  };

  const adjust = (lines: AdjustEntryIngredientInput[]) => {
    onAdjust(lines);
    // Même règle qu'à la validation d'une entrée : la mise à jour optimiste
    // prend le relais, on ne fait pas attendre l'utilisateur (§6.2).
    onClose();
  };

  if (entry) {
    return (
      <CorrectionBody
        entry={entry}
        mealName={mealName}
        onSubmit={validate}
        onAdjust={adjust}
      />
    );
  }

  if (stage.kind === 'quantity') {
    return (
      <QuantityStep
        pick={stage.pick}
        mealName={mealName}
        onBack={() => {
          setStage({ kind: 'browse' });
        }}
        onValidate={validate}
      />
    );
  }

  if (stage.kind === 'recipe') {
    return (
      <RecipeEntryStep
        recipe={stage.recipe}
        mealName={mealName}
        onBack={() => {
          setStage({ kind: 'browse' });
        }}
        onValidate={validate}
      />
    );
  }

  if (stage.kind === 'create') {
    return (
      <CreateFoodStage
        draft={stage.draft}
        onDraftChange={(draft) => {
          setStage({ kind: 'create', draft });
        }}
        onCreated={(pick) => {
          setStage({ kind: 'quantity', pick });
        }}
        onBack={() => {
          setStage({ kind: 'browse' });
        }}
      />
    );
  }

  const pick = (chosen: AddPick) => {
    setStage(
      chosen.kind === 'recipe'
        ? { kind: 'recipe', recipe: chosen.recipe }
        : { kind: 'quantity', pick: chosen },
    );
  };

  return (
    <>
      <CategoryStrip category={category} onChange={setCategory} />

      {category === 'food' && (
        <>
          <TabStrip tabs={FOOD_TABS} tab={foodTab} onChange={setFoodTab} />

          {foodTab === 'quick' && <QuickAccessPanel scope="food" onPick={pick} />}

          {foodTab === 'search' && (
            <SearchPanel
              scope="food"
              onPick={pick}
              onCreate={() => {
                setStage({ kind: 'create', draft: emptyFoodDraft() });
              }}
            />
          )}

          {/*
            ⚠️ Monté uniquement quand l'onglet est actif : c'est ce qui garantit
            que la caméra n'est jamais demandée au chargement de l'application
            (§2), alors même que cette feuille reste montée en permanence (§6.2).
            Changer de **catégorie** démonte donc aussi le scanner, ce qui libère
            le flux vidéo par le troisième chemin de sortie de l'étape 6.
          */}
          {foodTab === 'scan' && (
            <ScanPanel
              onPick={pick}
              onUnknownCode={(code) => {
                setStage({ kind: 'create', draft: emptyFoodDraft(code) });
              }}
            />
          )}

          {foodTab === 'free' && (
            <FreeEntryForm submitLabel={`Ajouter à ${mealName}`} onSubmit={validate} />
          )}
        </>
      )}

      {category === 'meal' && (
        <MealPanel
          currentMealId={mealId}
          onPick={(sourceMealId) => {
            onCopyMeal(sourceMealId);
            // Même règle qu'à la validation d'une entrée : on ne fait pas
            // attendre l'utilisateur devant la feuille (§6.2).
            onClose();
          }}
        />
      )}

      {category === 'recipe' && (
        <>
          <TabStrip tabs={RECIPE_TABS} tab={recipeTab} onChange={setRecipeTab} />

          {recipeTab === 'quick' && <QuickAccessPanel scope="recipe" onPick={pick} />}

          {recipeTab === 'search' && (
            <SearchPanel
              scope="recipe"
              onPick={pick}
              onCreate={() => {
                setStage({ kind: 'create', draft: emptyFoodDraft() });
              }}
            />
          )}
        </>
      )}
    </>
  );
}

export function EntrySheet({
  open,
  mealId,
  mealName,
  entry,
  onSubmit,
  onAdjust,
  onCopyMeal,
  onClose,
}: {
  open: boolean;
  /** Repas visé. `undefined` quand la feuille est fermée. */
  mealId: number | undefined;
  mealName: string;
  /** Entrée à corriger, ou `undefined` pour une création. */
  entry?: LogEntryDTO;
  onSubmit: (input: LogEntryInput, preview: EntryPreview) => void;
  /** Correction des quantités d'un bloc recette (specs §6.5-d). */
  onAdjust: (lines: AdjustEntryIngredientInput[]) => void;
  /** Recopie un repas déjà composé dans celui-ci. */
  onCopyMeal: (sourceMealId: number) => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={open}
      title={entry ? `Corriger — ${mealName}` : `Ajouter à ${mealName}`}
      onClose={onClose}
    >
      {/*
        `key` : passer d'une entrée à une autre sans refermer la feuille
        reconstruit le corps au lieu de garder l'étape précédente.
      */}
      <EntryBody
        key={entry ? `entry-${String(entry.id)}` : `new-${mealName}`}
        entry={entry}
        mealId={mealId}
        mealName={mealName}
        onSubmit={onSubmit}
        onAdjust={onAdjust}
        onCopyMeal={onCopyMeal}
        onClose={onClose}
      />
    </Sheet>
  );
}
